import {
  useMentorProfileReview,
  type MentorProfileReview,
} from "@/lib/api/mentor-profile";
import { useCurrentUser, UserRole } from "@/lib/api/user";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

const ALLOWLIST_PREFIXES = [
  "/training",
  "/settings",
  "/conversations",
  "/support",
  "/mentor-profile",
];

function isAllowlisted(pathname: string) {
  return ALLOWLIST_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Only mentors who were sent an invite and have not published yet stay on
 * onboarding. GET /me get-or-creates an unpublished review for any mentor who
 * loads the dashboard — that must not trap existing mentors.
 */
export function mustCompleteMatchingProfileOnboarding(
  review: Pick<MentorProfileReview, "invite_sent_at" | "published_snapshot"> | null | undefined,
): boolean {
  return Boolean(review?.invite_sent_at) && !review?.published_snapshot;
}

/**
 * Mentors invited into matching-profile onboarding stay on the wizard until
 * publish. Existing mentors (no invite) keep full dashboard access. Admins
 * are never force-gated.
 */
export function useMentorOnboardingGate() {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isMentor =
    user?.role === UserRole.MENTOR || isMentorIdentity(user?.systemRoles);
  const isAdmin =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERADMIN;
  const isMentorOnly = isMentor && !isAdmin;

  const { data: review, isPending: reviewPending } = useMentorProfileReview(
    Boolean(isMentorOnly),
  );

  const mustOnboard = mustCompleteMatchingProfileOnboarding(review);
  const shouldRedirect =
    isMentorOnly && mustOnboard && !isAllowlisted(pathname);

  // Hold the dashboard until review resolves — otherwise SPA login mounts `/`
  // before `/mentor-profile/me` returns and the mentor can use the app until refresh.
  const isGating =
    shouldRedirect ||
    (isMentorOnly && reviewPending && !isAllowlisted(pathname));

  useEffect(() => {
    if (!shouldRedirect) return;
    void navigate({ to: "/mentor-profile", replace: true });
  }, [navigate, shouldRedirect, pathname]);

  return { isGating };
}
